import { useState, useRef } from 'react';
import styles from './ProductModal.module.css';

const CATEGORIES = ['Tea Leaves', 'Coffee', 'Syrups', 'Dairy', 'Boba Supplies', 'Flavored Powders', 'Cups & Straws', 'Stationery'];
const DEMAND_WEIGHTS = [
  { label: 'Low',    value: 1 },
  { label: 'Medium', value: 2 },
  { label: 'High',   value: 3 },
];

const EMPTY_FORM = {
  id: '', name: '', category: 'Tea Leaves', unit: '', description: '',
  price: '', stock: '', demandWeight: 2,
};

export default function ProductModal({ mode = 'add', initialData = {}, onSave, onCancel }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState({ ...EMPTY_FORM, ...initialData });
  const [errors, setErrors] = useState({});
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(initialData.imageUrl || null);
  const fileInputRef = useRef(null);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  }

  function validate() {
    const e = {};
    if (!form.id.trim())   e.id    = 'Required';
    if (!form.name.trim()) e.name  = 'Required';
    if (!form.price || Number(form.price) <= 0) e.price = 'Enter a valid price';
    if (!form.stock || Number(form.stock) < 0)  e.stock = 'Enter a valid quantity';
    return e;
  }

  function handleImageChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }

  function handleRemoveImage() {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    onSave({
      id:           form.id.trim(),
      name:         form.name.trim(),
      category:     form.category,
      unit:         form.unit.trim(),
      description:  form.description.trim(),
      price:        Number(form.price),
      stock:        Number(form.stock),
      demandWeight: Number(form.demandWeight),
      imageFile,
      imageUrl:     imagePreview && !imageFile ? imagePreview : null,
    });
  }

  return (
    <div className={styles.overlay} onClick={onCancel}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <h3 className={styles.modalTitle}>
          {isEdit ? 'Edit Product' : 'Add New Product'}
        </h3>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.row2}>
            <Field label="Product ID" error={errors.id}>
              <input
                className={`${styles.input} ${errors.id ? styles.inputError : ''}`}
                value={form.id}
                onChange={e => set('id', e.target.value)}
                disabled={isEdit}
                placeholder="e.g. CFE-004"
              />
            </Field>

            <Field label="Category">
              <select
                className={styles.input}
                value={form.category}
                onChange={e => set('category', e.target.value)}
              >
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <Field label="Product Name" error={errors.name}>
            <input
              className={`${styles.input} ${errors.name ? styles.inputError : ''}`}
              value={form.name}
              onChange={e => set('name', e.target.value)}
              placeholder="e.g. Black Tea"
            />
          </Field>

          <Field label="Unit / Package Size" hint="e.g. 1kg, 500g, 100 pcs">
            <input
              className={styles.input}
              value={form.unit}
              onChange={e => set('unit', e.target.value)}
              placeholder="e.g. 1kg"
            />
          </Field>

          <Field label="Description">
            <textarea
              className={`${styles.input} ${styles.textarea}`}
              rows={3}
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Short product description…"
            />
          </Field>

          <div className={styles.row2}>
            <Field label="Unit Price (₱)" error={errors.price}>
              <input
                className={`${styles.input} ${errors.price ? styles.inputError : ''}`}
                type="number"
                min="0"
                step="0.01"
                value={form.price}
                onChange={e => set('price', e.target.value)}
                placeholder="0.00"
              />
            </Field>

            <Field label={isEdit ? 'Stock Quantity' : 'Initial Stock Quantity'} error={errors.stock}>
              <input
                className={`${styles.input} ${errors.stock ? styles.inputError : ''}`}
                type="number"
                min="0"
                value={form.stock}
                onChange={e => set('stock', e.target.value)}
                placeholder="0"
              />
            </Field>
          </div>

          <Field
            label="Demand Weight"
            hint="Used by the WFQ ordering algorithm"
          >
            <select
              className={styles.input}
              value={form.demandWeight}
              onChange={e => set('demandWeight', e.target.value)}
            >
              {DEMAND_WEIGHTS.map(d => (
                <option key={d.value} value={d.value}>{d.label} ({d.value})</option>
              ))}
            </select>
          </Field>

          <div className={styles.field}>
            <span className={styles.fieldLabel}>Product Photo</span>
            {imagePreview ? (
              <div className={styles.imagePreviewWrap}>
                <img src={imagePreview} alt="preview" className={styles.imagePreview} />
                <button type="button" className={styles.removeImageBtn} onClick={handleRemoveImage}>
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                className={styles.addPhotoBtn}
                onClick={() => fileInputRef.current?.click()}
              >
                + Add Photo
              </button>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
          </div>

          <div className={styles.actions}>
            <button type="button" className={styles.cancelBtn} onClick={onCancel}>
              Cancel
            </button>
            <button type="submit" className={styles.saveBtn}>
              {isEdit ? 'Save Changes' : 'Add Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, error, children }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {hint && <span className={styles.fieldHint}>{hint}</span>}
      {children}
      {error && <span className={styles.fieldError}>{error}</span>}
    </label>
  );
}
